import { ChangeDetectionStrategy, Component, HostListener, inject, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { initPsicIcons } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { HeaderComponent } from './shared/components/header/header.component';
import { NavigationMenuComponent } from './shared/components/navigation-menu/navigation-menu.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { CardLoaderComponent } from './shared/components/card-loader/card-loader.component';
import { LandingPageService } from './shared/services/landing-page.service';
import { GoBackService } from './shared/services/go-back.service';
import { PageLoaderService } from './shared/services/page-loader.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    NavigationMenuComponent,
    ToastComponent,
    CardLoaderComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  title = 'Claudia UI Portal';

  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly landingPageService = inject(LandingPageService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly goBackService = inject(GoBackService);
  readonly pageLoaderService = inject(PageLoaderService);

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: any): void {
    if (this.goBackService.beforeUnloadAlert) {
      event.returnValue = false;
    }
  }

  readonly landingPage = toSignal(this.landingPageService.landingPage$, { initialValue: true });

  /** Reads `hideSideMenu` from the deepest activated route's data */
  readonly showSideMenu = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return !route.snapshot.data['hideSideMenu'];
      })
    ),
    { initialValue: !this.getDeepestRouteData() }
  );

  readonly isDocumentUploadRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => {
        const url = (e as NavigationEnd).urlAfterRedirects;
        return url.includes('documentupload') || url.includes('snl');
      })
    ),
    {
      initialValue:
        window.location.href.includes('documentupload') || window.location.href.includes('snl'),
    }
  );

  readonly isSnlRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects.includes('snl'))
    ),
    { initialValue: window.location.href.includes('snl') }
  );

  constructor(private renderer: Renderer2) {
    this.registerIcons();
    this.applyTheme(this.selectedTheme);
  }

  /** Read initial route data on app bootstrap */
  private getDeepestRouteData(): boolean {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return !!route.snapshot.data['hideSideMenu'];
  }

  private registerIcons(): void {
    this.iconRegistry.addSvgIconResolver((name: string) =>
      this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/images/${name}.svg`)
    );
    initPsicIcons(this.sanitizer, this.iconRegistry);
  }

  selectedTheme: string = localStorage.getItem('selectedTheme') || 'psic';
  onThemeChange(theme: string) {
    this.selectedTheme = theme;
    this.applyTheme(theme);
  }

  private applyTheme(theme: string) {
    document.body.classList.remove('red', 'green', 'psic', 'blue', 'neutral', 'dark');
    this.renderer.addClass(document.body, theme);
  }
}
